import classnames from 'classnames';
import {
  useEffect,
  useState,
  type ChangeEventHandler,
  type FocusEvent,
  type FocusEventHandler,
  type FunctionComponent,
} from 'react';

import { forgeTestId } from '@/core/tamagoshi/utils/forgeTestId';

import { Button } from '../Button/Button';
import { Minus as IconMinus } from '../icons/Minus';
import { Plus as IconPlus } from '../icons/Plus';
import { Input, type InputProps } from '../Input/Input';
import getCappedMaximum from './utils/getCappedMaximum';
import getCappedValue from './utils/getCappedValue';

import styles from './StepperInput.module.scss';

export interface StepperInputProps extends Omit<InputProps, 'onChange'> {
  /**
   * To override the style for input
   */
  className?: string;
  /**
   * The step prop specifies the interval between legal numbers
   */
  step?: number;
  /**
   * Minimum value of input
   */
  min?: number;
  /**
   * Maximum value of input
   */
  max?: number;
  /**
   * onChange callback
   */
  onBlur?: (event: FocusEvent<HTMLInputElement>) => unknown;
  onChange?: (value: number) => unknown;
  onMaximumReached?: (value: string | number, maximum: number) => unknown;
  onMinimumReached?: (value: string | number, minimum: number) => unknown;
  isDisabled?: boolean;
  incrementLabel?: string;
  decrementLabel?: string;
  value?: number;
  'data-testid'?: string;
  id?: string;
}

export const StepperInput: FunctionComponent<StepperInputProps> = ({
  className,
  'data-testid': testId,
  decrementLabel,
  incrementLabel,
  isDisabled = false,
  max,
  min,
  onBlur,
  onChange,
  onMaximumReached,
  onMinimumReached,
  step = 1,
  value: valueFromProps,
  ...restProps
}) => {
  const [isEmpty, setIsEmpty] = useState(false);
  const [increment, setIncrement] = useState<number>();
  const [minimum, setMinimum] = useState<number>();
  const [maximum, setMaximum] = useState<number>();
  const [value, setValue] = useState<number>();
  useEffect(() => {
    const newIncrement =
      step !== undefined && step !== null
        ? Math.max(
            (typeof step === 'string' ? parseInt(step, 10) : step) || 0,
            1,
          )
        : undefined;
    const newMinimum =
      min !== undefined && min !== null
        ? (typeof min === 'string' ? parseInt(min, 10) : min) || 0
        : undefined;
    const newMaximum = getCappedMaximum(newMinimum, max, newIncrement);
    setIncrement(newIncrement);
    setMinimum(newMinimum);
    setMaximum(newMaximum);
  }, [min, max, step]);
  useEffect(() => {
    const newValue = getCappedValue(
      valueFromProps,
      minimum,
      maximum,
      increment,
    );
    setValue(newValue);
  }, [valueFromProps, minimum, maximum, increment]);

  const updateValue = (updatedValue: string | number, allowEmpty: boolean) => {
    // to handle keyboard properly
    if (allowEmpty && updatedValue === '') {
      setIsEmpty(true);
      return;
    }

    setIsEmpty(false);
    const newUpdatedValue =
      (typeof updatedValue === 'string'
        ? parseInt(updatedValue, 10)
        : updatedValue) || 0;
    if (
      onMinimumReached &&
      minimum !== undefined &&
      newUpdatedValue <= minimum
    ) {
      onMinimumReached(updatedValue, minimum);
    }

    if (
      onMaximumReached &&
      maximum !== undefined &&
      newUpdatedValue >= maximum
    ) {
      onMaximumReached(updatedValue, maximum);
    }

    const newValue = getCappedValue(
      newUpdatedValue,
      minimum,
      maximum,
      increment,
    );

    if (onChange && newValue !== undefined && newValue !== value) {
      setValue(newValue);
      onChange(newValue); // debounce onChange ?
    }
  };

  const changeHandler: ChangeEventHandler<HTMLInputElement> = (event) => {
    updateValue(event.target.value, true);
  };
  const blurHandler: FocusEventHandler<HTMLInputElement> = (event) => {
    if (isEmpty && value) {
      updateValue(value, false);
    }
    if (onBlur) {
      onBlur(event);
    }
  };
  const decrementHandler = () => {
    if (increment && typeof value === 'number') {
      updateValue(value - increment, false);
    }
  };
  const incrementhandler = () => {
    if (increment && typeof value === 'number') {
      updateValue(value + increment, false);
    }
  };

  return (
    <Input
      {...restProps}
      className={classnames(styles.stepper, className)}
      inputClassName={classnames([styles.input])}
      value={isEmpty ? '' : (value ?? '')}
      onChange={changeHandler}
      onBlur={blurHandler}
      type="number"
      isDisabled={isDisabled}
      step={increment}
      min={minimum}
      max={maximum}
      data-testid={testId}
      prefix={
        <Button
          tabIndex={-1} // handled by down arrow key natively
          onClick={decrementHandler}
          className={styles.action}
          aria-label={decrementLabel}
          isDisabled={
            isDisabled ||
            (minimum !== undefined && value !== undefined && minimum >= value)
          }
          data-testid={forgeTestId(testId, 'decrement-cta')}
          icon={<IconMinus />}
          theme="none"
          variant="none"
        />
      }
      suffix={
        <Button
          tabIndex={-1} // handled by up arrow key natively
          onClick={incrementhandler}
          className={styles.action}
          aria-label={incrementLabel}
          isDisabled={
            isDisabled ||
            (maximum !== undefined && value !== undefined && maximum <= value)
          }
          data-testid={forgeTestId(testId, 'increment-cta')}
          icon={<IconPlus />}
          theme="none"
          variant="none"
        />
      }
    />
  );
};

StepperInput.displayName = 'StepperInput';
